<template lang="pug">
  yz-card.px-6.py-5(color="white")
    .d-flex.justify-space-between.align-center.mb-3
      .yz-heading-8 {{ $t('panel.documents') }}
      yz-btn(:label="$t('add')" size="sm" outlined, prepend-icon="$yzi-plus" icon-color="yz-neutral-500" background-color="white" @click="onAddDocument")

    // Empty state for documents
    yz-empty-state-card(v-if="documentsData.length === 0" icon="$yzi-file-question-02" icon-size="32px" icon-color="yz-neutral-500" :title="$t('panel.no_document_added')" :description="$t('panel.added_document_will_show_here')" :action-label="$t('panel.add_document')" action-size="sm" border-color="yz-neutral-400" @action-click="onAddDocument")

    // Documents Data Table
    yz-data-table(v-if="documentsData.length > 0" :headers="documentHeaders" :items="documentsData" :loading="isRefreshing")
      template(v-slot:item.actions="{ item }")
        yz-menu(offset-y attach)
          template(v-slot:activator="{ on, attrs }")
            yz-btn(v-bind="attrs" v-on="on" icon="$yzi-dots-horizontal" size="sm" icon-only background-color="white")
          yz-list
            yz-list-item(@click="onViewDocument(item)")
              div {{ $t('panel.view') }}
            yz-divider(color="yz-neutral-300")
            yz-list-item(@click="onDeleteDocument(item)")
              .text-danger-500 {{ $t('delete') }}

    // Add Document Dialog
    add-document-dialog(v-if="showAddDocumentDialog" v-model="showAddDocumentDialog" :parent-claim="parentClaim" @document-added="onDocumentAdded")

    // Delete Document Confirmation Dialog
    yz-delete-confirmation-dialog(v-if="showDeleteDocumentDialog" v-model="showDeleteDocumentDialog" item="document" :file-data="[documentToDelete]" :loading="isDeletingDocument" @confirmed="onConfirmDeleteDocument" @canceled="onCancelDeleteDocument")

    // View Document Dialog
    view-document-dialog(v-if="showViewDocumentDialog" v-model="showViewDocumentDialog" :parent-claim="parentClaim" @updated="$emit('updated')")
</template>

<script>
import { DateTime } from 'luxon'
import { mapActions } from 'vuex'
import AddDocumentDialog from '@/components/clinic/panel/dialogs/AddDocumentDialog.vue'
import ViewDocumentDialog from '@/components/clinic/panel/dialogs/ViewDocumentDialog.vue'

export default {
  components: { AddDocumentDialog, ViewDocumentDialog },
  props: {
    parentClaim: {
      type: Object,
      required: true
    },
    isRefreshing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      documentHeaders: [
        { text: this.$t('panel.document_name'), value: 'name', align: 'start' },
        { text: this.$t('panel.type'), value: 'type', align: 'start' },
        { text: this.$t('panel.size'), value: 'size', align: 'start' },
        { text: this.$t('panel.date'), value: 'date', align: 'start' },
        { text: this.$t('panel.uploaded_by'), value: 'uploadedBy', align: 'start' },
        { text: '', value: 'actions', align: 'end', sortable: false, width: '56px' }
      ],
      showAddDocumentDialog: false,
      showDeleteDocumentDialog: false,
      showViewDocumentDialog: false,
      documentToDelete: null,
      isDeletingDocument: false
    }
  },
  computed: {
    documentsData() {
      if (!this.parentClaim?.documents || !Array.isArray(this.parentClaim.documents)) {
        return []
      }

      return this.parentClaim.documents.map(doc => ({
        ...doc,
        id: doc.hid,
        name: doc.filename,
        type: this.getFileExtension(doc.type),
        rawType: doc.type,
        size: this.formatFileSize(doc.size),
        rawSize: doc.size,
        date: this.formatDate(doc.created),
        uploadedBy: this.getUploaderName(doc.uploaded_by)
      }))
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    getFileExtension(mimeType) {
      if (!mimeType) return '-'
      return mimeType
        .split('/')
        .pop()
        .toUpperCase()
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 KB'
      const kb = bytes / 1024
      if (kb < 1024) {
        return `${Math.round(kb)} KB`
      }
      const mb = kb / 1024
      return `${mb.toFixed(1)} MB`
    },
    formatDate(isoString) {
      if (!isoString) return '-'
      const date = DateTime.fromISO(isoString)
      return date.isValid ? date.toFormat('dd MMMM yyyy, HH:mm') : '-'
    },
    getUploaderName(uploadedBy) {
      if (!uploadedBy) return null
      const firstName = uploadedBy.first_name || ''
      const lastName = uploadedBy.last_name || ''
      return `${firstName} ${lastName}`.trim() || null
    },
    onAddDocument() {
      this.showAddDocumentDialog = true
    },
    onDocumentAdded() {
      this.$emit('updated')
    },
    onViewDocument() {
      this.showViewDocumentDialog = true
    },
    onDeleteDocument(item) {
      this.documentToDelete = item
      this.showDeleteDocumentDialog = true
    },
    async onConfirmDeleteDocument() {
      if (!this.documentToDelete) return

      this.isDeletingDocument = true
      try {
        // Call bulk delete endpoint with the document HID
        await this.$axios.post('/documents/bulk_delete', {
          documents: [this.documentToDelete.hid]
        })

        await this.showMessage(['success', this.$t('panel.document_deleted')])

        // Emit event to parent to refresh claim data
        this.$emit('updated', this.documentToDelete.id)

        this.showDeleteDocumentDialog = false
        this.documentToDelete = null
      } catch (error) {
        console.error('Error deleting document:', error)
        await this.showMessage(['error', this.$t('panel.failed_delete_document')])
      } finally {
        this.isDeletingDocument = false
      }
    },
    onCancelDeleteDocument() {
      this.showDeleteDocumentDialog = false
      this.documentToDelete = null
    }
  }
}
</script>

<style scoped></style>
